/**
 * Pure calculation engine for Relational Lookups & Rollups in Numtable.
 * Usable across both client browser (React / Ag-Grid) and Cloudflare Worker.
 */

export type RollupAggregate = 'SUM' | 'AVG' | 'COUNT' | 'MIN' | 'MAX';
export type RollupColumnType = 'rollup' | 'lookup';

export interface RollupConfig {
  /** 'rollup' performs aggregation (SUM, AVG, COUNT, MIN, MAX); 'lookup' retrieves linked field values */
  readonly type: RollupColumnType;
  /** Field/column ID in current sheet that contains the foreign relation (e.g. smartchip field) */
  readonly sourceCol: string;
  /** Target document ID (omitted or null for sibling tabs in the same document) */
  readonly targetDoc?: string | null;
  /** Target sheet/tab ID */
  readonly targetTab: string;
  /** Field in target tab to match against sourceCol (defaults to 'id') */
  readonly targetMatchCol?: string;
  /** Field in target tab to retrieve and summarize */
  readonly targetCol: string;
  /** Aggregation function (required for type 'rollup') */
  readonly agg?: RollupAggregate;
  /** Separator for multi-value lookups (defaults to ', ') */
  readonly separator?: string;
}

/**
 * Extracts a field value from a row, checking direct property,
 * computed dictionary, data payload, or id.
 */
export function getRowFieldValue(row: Record<string, unknown>, field: string): unknown {
  if (row[field] !== undefined) return row[field];
  if (row.computed && typeof row.computed === 'object' && (row.computed as Record<string, unknown>)[field] !== undefined) {
    return (row.computed as Record<string, unknown>)[field];
  }
  if (row.data && typeof row.data === 'object' && (row.data as Record<string, unknown>)[field] !== undefined) {
    return (row.data as Record<string, unknown>)[field];
  }
  if (field === 'id' && (row as { id?: unknown }).id !== undefined) {
    return (row as { id?: unknown }).id;
  }
  return undefined;
}

/**
 * Coerces numbers, currency strings ("$1,200.50", "1.234,56 €"),
 * percentages ("15%"), accounting parentheses ("(50)"), and booleans.
 */
export function coerceNumeric(val: unknown): number | null {
  if (typeof val === 'number') return Number.isFinite(val) ? val : null;
  if (typeof val === 'boolean') return val ? 1 : 0;
  if (typeof val !== 'string') return null;

  let str = val.trim();
  if (str === '' || str === '-' || str === '—' || str.toLowerCase() === 'n/a' || str.toLowerCase() === 'null') {
    return null;
  }

  // Check parenthesized negative: (123.45) -> -123.45
  let isNegative = false;
  if (str.startsWith('(') && str.endsWith(')')) {
    isNegative = true;
    str = str.slice(1, -1).trim();
  } else if (str.startsWith('-')) {
    isNegative = true;
    str = str.slice(1).trim();
    if (str.startsWith('-') || str.startsWith('+')) return null; // Reject malformed double signs like --50
  } else if (str.startsWith('+')) {
    str = str.slice(1).trim();
    if (str.startsWith('-') || str.startsWith('+')) return null;
  }

  // Check percentage
  const isPercent = str.endsWith('%');
  if (isPercent) {
    str = str.slice(0, -1).trim();
  }

  // Preserve scientific notation exponent (e.g. 1e5, 1.5e3, 2e-3)
  const sciMatch = str.match(/(?<=\d)[eE][+-]?\d+/);
  let sciExponent = '';
  if (sciMatch && sciMatch.index !== undefined && sciMatch.index > 0) {
    sciExponent = sciMatch[0];
    str = str.replace(sciExponent, '\uFFF0');
  }

  // Strip currency symbols, spaces, and letter codes ($, €, £, ¥, ₹, R$, USD, etc.)
  str = str.replace(/[$€£¥₹₽]/g, '').replace(/[A-Za-z\s]/g, '');

  // Restore scientific exponent
  if (sciExponent) {
    str = str.replace('\uFFF0', sciExponent);
  }

  if (str === '') return null;

  // Handle European vs Standard decimal/thousands separators:
  // e.g. "1.234,56" -> "1234.56" ; "1,234.56" -> "1234.56"
  if (str.includes(',') && str.includes('.')) {
    if (str.lastIndexOf(',') > str.lastIndexOf('.')) {
      str = str.replace(/\./g, '').replace(',', '.');
    } else {
      str = str.replace(/,/g, '');
    }
  } else if (str.includes(',')) {
    const commaCount = (str.match(/,/g) || []).length;
    // Multiple commas (1,000,000) or standard 3-digit grouping (1,000) are thousands separators
    if (commaCount > 1 || /^\d{1,3},\d{3}$/.test(str)) {
      str = str.replace(/,/g, '');
    } else {
      // Single comma with decimals (12,50 or 0,75)
      str = str.replace(',', '.');
    }
  }

  const num = Number(str);
  if (!Number.isFinite(num)) return null;

  let result = isPercent ? num / 100 : num;
  if (isNegative) result = -result;
  return result;
}

/** Parses dates and timestamps into epoch millisecond timestamps */
export function coerceDateTimestamp(val: unknown): number | null {
  if (val instanceof Date) {
    const t = val.getTime();
    return Number.isNaN(t) ? null : t;
  }
  if (typeof val === 'number') {
    if (!Number.isFinite(val)) return null;
    // Epoch milliseconds or seconds
    if (val > 10000000000) return val;
    // Excel serial dates roughly between 1900 and 2170 (1000..100000)
    if (val > 1000 && val < 100000) {
      // Excel base is 1899-12-30 (accounting for 1900 leap year bug)
      const ms = Math.round((val - 25569) * 86400 * 1000);
      return Number.isNaN(ms) ? null : ms;
    }
    return null;
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed) return null;

    // DD/MM/YYYY or DD-MM-YYYY
    const ddmmyyyy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(trimmed);
    if (ddmmyyyy) {
      const parsed = new Date(Number(ddmmyyyy[3]), Number(ddmmyyyy[2]) - 1, Number(ddmmyyyy[1]));
      return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
    }

    const t = Date.parse(trimmed);
    if (!Number.isNaN(t)) return t;
  }
  return null;
}

/** Computes SUM across values, ignoring non-numerics. Returns 0 for empty values. */
export function rollupSum(values: readonly unknown[]): number {
  let sum = 0;
  for (const v of values) {
    const n = coerceNumeric(v);
    if (n !== null) sum += n;
  }
  return Math.round(sum * 1e10) / 1e10;
}

/** Computes AVG across values. Returns null when empty or no numeric values exist. */
export function rollupAvg(values: readonly unknown[]): number | null {
  let sum = 0;
  let count = 0;
  for (const v of values) {
    const n = coerceNumeric(v);
    if (n !== null) {
      sum += n;
      count++;
    }
  }
  if (count === 0) return null;
  return Math.round((sum / count) * 1e10) / 1e10;
}

/** Computes COUNT of non-empty records. Returns 0 for empty array. */
export function rollupCount(values: readonly unknown[]): number {
  let count = 0;
  for (const v of values) {
    if (v !== null && v !== undefined && v !== '') {
      count++;
    }
  }
  return count;
}

/** Computes MIN value (supports numbers, dates, and text). Returns null when empty. */
export function rollupMin(values: readonly unknown[]): number | string | null {
  const valid = values.filter((v) => v !== null && v !== undefined && v !== '');
  if (valid.length === 0) return null;

  // Check if all are dates
  const dateEntries = valid.map((v) => ({ original: v, ts: coerceDateTimestamp(v) }));
  if (dateEntries.every((e) => e.ts !== null)) {
    let minEntry = dateEntries[0]!;
    for (let i = 1; i < dateEntries.length; i++) {
      if (dateEntries[i]!.ts! < minEntry.ts!) {
        minEntry = dateEntries[i]!;
      }
    }
    return String(minEntry.original);
  }

  // Check if all are numeric
  const nums: number[] = [];
  for (const v of valid) {
    const n = coerceNumeric(v);
    if (n !== null) nums.push(n);
  }
  if (nums.length > 0) {
    let min = nums[0]!;
    for (let i = 1; i < nums.length; i++) {
      if (nums[i]! < min) min = nums[i]!;
    }
    return min;
  }

  // Fallback to lexicographical comparison
  return valid.map((v) => String(v)).sort((a, b) => a.localeCompare(b))[0] ?? null;
}

/** Computes MAX value (supports numbers, dates, and text). Returns null when empty. */
export function rollupMax(values: readonly unknown[]): number | string | null {
  const valid = values.filter((v) => v !== null && v !== undefined && v !== '');
  if (valid.length === 0) return null;

  // Check if all are dates
  const dateEntries = valid.map((v) => ({ original: v, ts: coerceDateTimestamp(v) }));
  if (dateEntries.every((e) => e.ts !== null)) {
    let maxEntry = dateEntries[0]!;
    for (let i = 1; i < dateEntries.length; i++) {
      if (dateEntries[i]!.ts! > maxEntry.ts!) {
        maxEntry = dateEntries[i]!;
      }
    }
    return String(maxEntry.original);
  }

  // Check if all are numeric
  const nums: number[] = [];
  for (const v of valid) {
    const n = coerceNumeric(v);
    if (n !== null) nums.push(n);
  }
  if (nums.length > 0) {
    let max = nums[0]!;
    for (let i = 1; i < nums.length; i++) {
      if (nums[i]! > max) max = nums[i]!;
    }
    return max;
  }

  // Fallback to lexicographical comparison
  return valid.map((v) => String(v)).sort((a, b) => b.localeCompare(a))[0] ?? null;
}

/** Computes aggregate summary according to the specified aggregation function */
export function computeRollup(
  aggregate: RollupAggregate,
  values: readonly unknown[],
): number | string | null {
  switch (aggregate) {
    case 'SUM':
      return rollupSum(values);
    case 'AVG':
      return rollupAvg(values);
    case 'COUNT':
      return rollupCount(values);
    case 'MIN':
      return rollupMin(values);
    case 'MAX':
      return rollupMax(values);
    default:
      return null;
  }
}

/** Resolves lookup values, deduplicating identical items and formatting as a single value or separated string */
export function resolveLookup(values: readonly unknown[], separator = ', '): unknown {
  const valid = values.filter((v) => v !== null && v !== undefined && v !== '');
  if (valid.length === 0) return null;

  const distinct: unknown[] = [];
  const seen = new Set<string>();
  for (const v of valid) {
    const s = String(v);
    if (!seen.has(s)) {
      seen.add(s);
      distinct.push(v);
    }
  }

  if (distinct.length === 1) return distinct[0];
  return distinct.map((v) => String(v)).join(separator);
}

/**
 * Builds an O(1) inverted hash index mapping normalized match key -> list of target rows.
 */
export function buildTargetRowIndex(
  rows: readonly Record<string, unknown>[],
  matchCol: string,
): Map<string, Record<string, unknown>[]> {
  const index = new Map<string, Record<string, unknown>[]>();
  for (const row of rows) {
    const raw = getRowFieldValue(row, matchCol);
    if (raw === null || raw === undefined) continue;

    const items = Array.isArray(raw) ? raw : [raw];
    for (const item of items) {
      if (item === null || item === undefined) continue;
      const key = String(item).trim().toLowerCase();
      if (!key) continue;
      const existing = index.get(key);
      if (existing) {
        existing.push(row);
      } else {
        index.set(key, [row]);
      }
    }
  }
  return index;
}

/**
 * Resolves values from targetRows matching the foreign key(s) in sourceRow.
 */
export function resolveLinkedTargetValues(
  sourceRow: Record<string, unknown>,
  config: RollupConfig,
  targetRows: readonly Record<string, unknown>[],
  targetIndex?: Map<string, Record<string, unknown>[]>,
): unknown[] {
  const rawSource = getRowFieldValue(sourceRow, config.sourceCol);
  if (rawSource === null || rawSource === undefined || rawSource === '') return [];

  const matchCol = config.targetMatchCol || 'id';
  const index = targetIndex ?? buildTargetRowIndex(targetRows, matchCol);

  const keysToMatch: string[] = [];
  if (Array.isArray(rawSource)) {
    for (const item of rawSource) {
      if (item !== null && item !== undefined) {
        const str = String(item).trim().toLowerCase();
        if (str) keysToMatch.push(str);
      }
    }
  } else {
    const directKey = String(rawSource).trim().toLowerCase();
    if (directKey) {
      // Prioritize direct match in target index before comma fragmentation
      if (index.has(directKey)) {
        keysToMatch.push(directKey);
      } else if (typeof rawSource === 'string' && rawSource.includes(',')) {
        const parts = rawSource.split(',');
        for (const p of parts) {
          const trimmed = p.trim().toLowerCase();
          if (trimmed) keysToMatch.push(trimmed);
        }
      } else {
        keysToMatch.push(directKey);
      }
    }
  }

  if (keysToMatch.length === 0) return [];

  const matchedRows: Record<string, unknown>[] = [];
  const seenRows = new Set<Record<string, unknown>>();

  for (const key of keysToMatch) {
    const found = index.get(key);
    if (found) {
      for (const r of found) {
        if (!seenRows.has(r)) {
          seenRows.add(r);
          matchedRows.push(r);
        }
      }
    }
  }

  return matchedRows.map((r) => getRowFieldValue(r, config.targetCol));
}

/**
 * Creates an Ag-Grid compatible valueGetter function with memoized hash index.
 */
export function createRollupValueGetter<T = Record<string, unknown>>(
  config: RollupConfig,
  targetRows: readonly Record<string, unknown>[],
): (params: { data?: T | undefined } | Record<string, unknown>) => unknown {
  const matchCol = config.targetMatchCol || 'id';
  const index = buildTargetRowIndex(targetRows, matchCol);

  return (params) => {
    if (!params) return null;
    const rawRow = 'data' in params && params.data ? params.data : params;
    if (!rawRow || typeof rawRow !== 'object') return null;
    const row = rawRow as Record<string, unknown>;
    const values = resolveLinkedTargetValues(row, config, targetRows, index);
    if (config.type === 'lookup') {
      return resolveLookup(values, config.separator);
    }
    return computeRollup(config.agg ?? 'SUM', values);
  };
}
